import { inject, Injectable, signal } from '@angular/core';
import { FileUtilities } from '../ui-components/au-file-loader/helpers/file-utilities';
import {
	IDroppedFile,
	IFileUploadEvent,
	IRequestObject,
	IUploadOptions,
} from '../ui-components/au-file-loader/au-file-loader.interfaces';
import { AuUtilsFunctions } from '../au-utils';
import { FileValidation } from '../ui-components/au-file-loader/helpers/file-validation.class';
import { fileTypesConfig } from '../ui-components/au-file-loader/au-file-loader.config';
import { filter, Observable, tap } from 'rxjs';
import { FileListTools } from '../ui-components/au-file-loader/helpers/file-list-helpers';
import { environment } from '@env/environment';
import { AuFileLoaderCore } from '../ui-components/au-file-loader/services/file-loader-core.service';
import { LocationInput } from '@app/pages/cmms/interfaces/cmms-global.interfaces';

const endpointsMap: Record<string, string> = {
	cmms: environment.cmmsGraphql,
	global: environment.graphql,
	federation: environment.federationGraphql,
	digitalTwin: environment.digitalTwinGraphql,
};

@Injectable({
	providedIn: 'root',
})
export class NewFileLoaderService extends FileListTools {
	private readonly fileLoaderCore = inject(AuFileLoaderCore);

	readonly fileValidation = signal<FileValidation>(new FileValidation(fileTypesConfig));
	readonly imageFileTypesConfig = signal<string[]>(
		fileTypesConfig['image'].supportedFormatsList
	);

	locations = signal<LocationInput[]>([]);

	fileTypesConfigToFileTypes(fileType: string = null): string {
		if (!fileType) {
			return this.imageFileTypesConfig()
				.map(format => `.${format}`)
				.join(',');
		}
		if (fileType === 'all') {
			return Object.keys(fileTypesConfig)
				.map(type => fileTypesConfig[type].supportedFormatsList)
				.flat()
				.map(format => `.${format}`)
				.join(',');
		}
		return (
			fileTypesConfig[fileType].supportedFormatsList
				?.map(format => `.${format}`)
				.join(',') || ''
		);
	}

	startLoadProcedure(files: File[], locations: LocationInput[]): void {
		for (const file of files) {
			this.locations.set(locations);
			const errors = this.fileValidation().getFileValidationErrors(file);
			const fileObject: Partial<IDroppedFile> = FileUtilities.makeObjectForNewFile(
				file,
				errors
			);
			this.filesList.update(currentFiles => [...currentFiles, fileObject]);
			const lastIndex = this.lastFileIndex;

			if (AuUtilsFunctions.objectNotEmpty(errors)) {
				this.filesList()[lastIndex]?.requestBrakeSubject.complete();
				return;
			}
			this.filesList.update(currentFiles => {
				const newFiles = [...currentFiles];
				if (newFiles[lastIndex]) {
					newFiles[lastIndex].file = file;
				}
				return newFiles;
			});
			this.startHttpLoading(file, lastIndex);
		}
	}

	startHttpLoading(file: File, index: number): void {
		const fileEntry = this.filesList()[index];
		const requestBrakeSubject$ = fileEntry?.requestBrakeSubject;

		if (!requestBrakeSubject$) {
			console.error(
				`File at index ${index} does not have a requestBrakeSubject$. Aborting upload.`
			);
			this.handleErrorEvent(
				{ message: 'Internal setup error: missing requestBrakeSubject$' },
				index
			);
			return;
		}

		const uploadOptions: IUploadOptions = {
			file,
			index,
			requestBrakeSubject$: requestBrakeSubject$,
			mutationVariables: { locations: this.locations() },
		};

		this.upload(uploadOptions)
			.pipe(
				tap(data => this.handleOngoingEvent(data)),
				filter(data => FileUtilities.finishEvent(data))
			)
			.subscribe({
				next: response => this.handleFinishEvent(response),
				error: actualError => this.handleErrorEvent(actualError, index),
			});
	}

	upload(uploadOption: IUploadOptions): Observable<IFileUploadEvent> {
		const endpoint: string = uploadOption.endpointName
			? endpointsMap[uploadOption.endpointName]
			: endpointsMap['cmms'];

		uploadOption.mutationVariables = uploadOption.mutationVariables || {};

		const request: IRequestObject = {
			file: uploadOption.file,
			index: uploadOption.index,
			requestBrakeSubject$: uploadOption.requestBrakeSubject$,
			httpUri: endpoint,
			mutation: uploadOption.mutation || null,
			mutationVariables: uploadOption.mutationVariables || null,
			locations: this.locations(),
		};

		return this.fileLoaderCore.makeRequest(request);
	}

	private handleOngoingEvent(data: IFileUploadEvent): void {
		const fileExists =
			data.index < this.filesList().length && this.filesList()[data.index];
		if (FileUtilities.progressEvent(data) && fileExists) {
			this.handleProgressEvent(data);
		}
	}

	private handleFinishEvent(response: IFileUploadEvent): void {
		this.updateFileInFilesList(response);
	}

	private handleErrorEvent(error: unknown, fileIndex: number): void {
		console.error(`Upload error for file at index ${fileIndex}:`, error);

		if (typeof fileIndex === 'number' && fileIndex >= 0) {
			this.filesList.update(currentFiles => {
				if (fileIndex < currentFiles.length) {
					const newFiles = [...currentFiles];
					const erroredFile = newFiles[fileIndex];

					if (erroredFile) {
						newFiles[fileIndex] = {
							...erroredFile,
						};
					}
					return newFiles;
				}
				return currentFiles;
			});
		} else {
			console.error(
				'Error event reported without a valid file index. Cannot update specific file status.',
				error
			);
		}
	}
}
